/**
 * The keyboard, in one place, because two tables are two chances to disagree.
 *
 * There were two: `App.tsx` bound lowercase `w/a/s/d`, and `tutorialSandbox.ts`
 * bound both cases with a comment saying the tables were deliberately identical.
 * They were not. With Caps Lock on — or Shift held, which a player leaning on
 * the keyboard does by accident — `event.key` is `'W'`, the round's lookup
 * missed, and the chef did not move *at all*, in a real round, after a tutorial
 * where the same keys worked. The arrows still worked, so it presented as
 * intermittent. Now there is one table and both screens import it.
 *
 * `event.key` is the right thing to read rather than `event.code`: `code` is
 * physical and would put a Dvorak player's hand on the wrong three keys, and
 * every other binding in this game is a character.
 */

import type { Direction } from '@overcooked/shared'

export const KEY_TO_DIRECTION: Readonly<Record<string, Direction>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  w: 'up',
  s: 'down',
  a: 'left',
  d: 'right',
  W: 'up',
  S: 'down',
  A: 'left',
  D: 'right',
}

/** The direction a key press means, or `undefined` for a key that is not one. */
export const directionFor = (key: string): Direction | undefined => KEY_TO_DIRECTION[key]

/**
 * A key press the game must keep its hands off.
 *
 * Cmd+R is "reload", Ctrl+S is "save", Cmd+A is "select all" — the browser's,
 * the operating system's, and never the kitchen's. Without this the round's
 * handler ate Cmd/Ctrl+R as the recipe-panel toggle and walked the chef sideways
 * on Cmd+A, because the modifier was never looked at. `shiftKey` is deliberately
 * *not* here: Shift+W is a player with Caps Lock or a heavy hand, and that is a
 * walk.
 */
export const isSystemChord = (event: {
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
}): boolean => event.ctrlKey || event.metaKey || event.altKey

/**
 * Whether the round's key handler should be listening at all.
 *
 * It used to be gated on `stage !== 'playing'` alone, which is true of the end
 * screen and of the lobby behind it — `ended` is separate state — so the handler
 * stayed installed after the whistle and its `preventDefault()` on Space meant
 * the "Play again" button could never be activated from the keyboard, and an
 * interact was sent into a finished round instead. `PracticeRound.tsx` already
 * gates on its own outcome; this is the same rule for the real thing.
 */
export const roundKeysActive = (state: {
  stage: string
  phase: string
  ended: boolean
}): boolean => state.stage === 'playing' && state.phase === 'playing' && !state.ended
